import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { get, isAuthenticated, post } from './services/api';
import { useUI } from './UIContext';
import { handleErr } from './utils/functions';
import { DEFAULT_RESOLVED_THEME, THEME_STORAGE_KEY, THEMES, normalizeTheme } from './constants/theme';

const ThemeContext = createContext();

function getSystemTheme() {
    if (!window.matchMedia) {
        return THEMES.LIGHT;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.LIGHT;
}

function getStoredTheme() {
    try {
        return normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
        return THEMES.SYSTEM;
    }
}

function persistTheme(theme) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, normalizeTheme(theme));
    } catch {
        // Ignore storage failures to avoid breaking theme updates.
    }
}

function applyThemeToDocument(theme) {
    const selectedTheme = normalizeTheme(theme);
    const resolvedTheme = selectedTheme === THEMES.SYSTEM
        ? getSystemTheme()
        : selectedTheme;

    document.documentElement.setAttribute('data-theme', selectedTheme);
    document.documentElement.setAttribute('data-theme-resolved', resolvedTheme);

    return resolvedTheme;
}

export function ThemeProvider({ children }) {
    const { setLoading, setMessage } = useUI();
    const [theme, setTheme] = useState(() => getStoredTheme());
    const [resolvedTheme, setResolvedTheme] = useState(DEFAULT_RESOLVED_THEME);
    const [isThemeReady, setIsThemeReady] = useState(() => !isAuthenticated());
    const hasLoadedRef = useRef(false);

    const loadTheme = useCallback(async (force = false, withLoading = true) => {
        if (!isAuthenticated()) {
            hasLoadedRef.current = false;
            setIsThemeReady(true);
            return;
        }

        if (window.location.pathname === '/api-unavailable') {
            return;
        }

        if (hasLoadedRef.current && !force) {
            setIsThemeReady(true);
            return;
        }

        if (withLoading) {
            setLoading(true);
        }

        setIsThemeReady(false);

        await get('/user-config/theme', true, setMessage,
            (response) => {
                const apiTheme = normalizeTheme(response?.data?.theme);
                setTheme(apiTheme);
                hasLoadedRef.current = true;
                setIsThemeReady(true);
            },
            (err) => {
                handleErr(setMessage, `Falha ao obter tema do utilizador: ${err}`);
                setIsThemeReady(true);
            },
            () => {
                if (withLoading) {
                    setLoading(false);
                }
            },
            'none'
        );
    }, [setLoading, setMessage]);

    const saveTheme = useCallback(async (nextTheme) => {
        const normalizedTheme = normalizeTheme(nextTheme);

        if (normalizedTheme === theme) {
            return;
        }

        setLoading(true);

        await post('/user-config/theme', { theme: normalizedTheme }, true, setMessage,
            () => {
                setTheme(normalizedTheme);
                persistTheme(normalizedTheme);
                window.location.reload();
            },
            (err) => {
                handleErr(setMessage, `Falha ao salvar tema do utilizador: ${err}`);
            },
            () => {
                setLoading(false);
            }
        );
    }, [theme, setLoading, setMessage]);

    useEffect(() => {
        loadTheme();
    }, [loadTheme]);

    useEffect(() => {
        function onAuthTokenChanged() {
            if (!isAuthenticated()) {
                hasLoadedRef.current = false;
                setIsThemeReady(true);
                return;
            }

            loadTheme(true, false);
        }

        window.addEventListener('auth-token-changed', onAuthTokenChanged);

        return () => {
            window.removeEventListener('auth-token-changed', onAuthTokenChanged);
        };
    }, [loadTheme]);

    useEffect(() => {
        const currentResolvedTheme = applyThemeToDocument(theme);
        persistTheme(theme);
        setResolvedTheme(currentResolvedTheme);
    }, [theme]);

    useEffect(() => {
        if (theme !== THEMES.SYSTEM || !window.matchMedia) {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const onSystemThemeChanged = () => {
            const currentResolvedTheme = applyThemeToDocument(THEMES.SYSTEM);
            setResolvedTheme(currentResolvedTheme);
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', onSystemThemeChanged);
        } else {
            mediaQuery.addListener(onSystemThemeChanged);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', onSystemThemeChanged);
            } else {
                mediaQuery.removeListener(onSystemThemeChanged);
            }
        };
    }, [theme]);

    const contextValue = useMemo(() => ({
        theme,
        resolvedTheme,
        isThemeReady,
        saveTheme,
        loadTheme,
    }), [theme, resolvedTheme, isThemeReady, saveTheme, loadTheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
